import { Container, Col, Row } from "react-bootstrap"
import styled from 'styled-components'
import { useState, useMemo, useCallback } from "react"
import Text from '~/components/Text'
import Box from "~/components/Box"
import Icon from '~/components/Icon'
import Link from '~/components/UI/Link'
import gsap from "~/modules/gsap"
import getSteps from "./content"
import breakpoints from "~/components/UI/breakpoints"
import ModalVideo from "~/components/UI/ModalVideo"
import { emitGA } from "~/utils/gtm"
import { useTranslation } from "~/i18n"
import Img from "~/components/UI/Image"
import { DesktopBoxContent } from "../Saving"
import config from "~/stores/Config"
import { observer } from "mobx-react-lite"

const MyContainer = styled(Container)`
  margin-bottom: 60px;
  background-color: #010413;
  min-height: 60px;
  top: 0;
  display: none;
  ${breakpoints.md} {
    display: block
  }
`

const TextItem = styled(Text) <{ active: boolean }>`
  margin: 16px 0;
  font-size: 18px;
  color: ${p => p.active ? 'white' : '#6c6c6c'};
  ${breakpoints.lg} {
    font-size: 20px;
  }
`

const Arrow = styled(Icon) <{ active?: boolean, transform?: string }>`
  font-size: 24px;
  color: ${p => p.active ? 'white' : '#6c6c6c'};
  transform: ${p => p.transform || 'none'};
`

const Image = styled(Img)`
  width: 100%;
  height: auto;
  position: absolute;
`


const Title = styled(Text)`
  font-size: 36px;
  ${breakpoints.md} {
    font-size: 48px;
  }
`

const GroupButtons: React.FC<{
  handleShow?: (evt: any) => void
}> = observer(() => {
  const [t] = useTranslation()
  return (
    <DesktopBoxContent
      profit={config.re?.interest + '%/' + t('năm')}
      term={t('>18 tháng')}
      minBalance={t('20 triệu')}
    />
  )
})

const DesktopIntro = () => {

  const [t] = useTranslation()
  const [current, setCurrent] = useState(1)
  const steps = useMemo(() => getSteps(t), [t])

  const changeStep = useCallback(({ next }: { next: number }) => {
    const duration = 0.32
    const distance = 30

    if (next > current) {
      gsap.fromTo(`.intro-step${current}`, {
        y: 0,
        opacity: 1
      }, {
        y: distance,
        opacity: 0,
        display: 'none',
        duration
      })

      gsap.fromTo(`.intro-step${next}`, {
        y: -distance,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        display: 'flex',
        duration
      })

    } else if (next < current) {
      gsap.fromTo(`.intro-step${next}`, {
        y: distance,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        display: 'flex',
        duration
      })

      gsap.fromTo(`.intro-step${current}`, {
        y: 0,
        opacity: 1
      }, {
        y: -distance,
        opacity: 0,
        display: 'none',
        duration
      })
    }

    setCurrent(next)
  }, [current])

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = useCallback((evt: any) => {
    evt.preventDefault();
    emitGA('Homepage', { label: 'Start', action: 'VideoPlay' })
    setShow(true);
  }, [])

  const onClickPrev = useCallback((evt: any) => {
    evt.preventDefault()
    if (current > 1) {
      const newCurrent = current - 1
      changeStep({ next: newCurrent })
    }
  }, [current])

  const onClickNext = (evt: any) => {
    evt.preventDefault()
    if (current < 3) {
      const newCurrent = current + 1
      changeStep({ next: newCurrent })
    }
  }

  const onClickStep = (evt: any) => {
    evt.preventDefault()
    const next: number = parseInt(evt.currentTarget.attributes['data-id'].value)
    changeStep({ next })
  }

  return (
    <MyContainer fluid>
      <ModalVideo src='https://www.youtube.com/embed/HHddbnpdO9U' show={show} onHide={handleClose} />
      <Container>
        <Row style={{ height: 650 }}>
          <Col md={2}
            style={{
              justifyContent: 'center',
              alignItems: 'flex-end',
              display: 'flex',
              flexDirection: 'column',
              padding: 0
            }}>
            <Link href='#' onClick={onClickPrev}>
              <Arrow active={current > 1} name='up' />
            </Link>
            <Link data-id='1' onClick={onClickStep} href='#'>
              <TextItem active={current === 1}>
                {t`Đầu Tư Chung`}
              </TextItem>
            </Link>
            <Link data-id='2' onClick={onClickStep} href='#'>
              <TextItem active={current === 2}>
                {t`Mua Bán Phần`}
              </TextItem>
            </Link>
            <Link data-id='3' onClick={onClickStep} href='#'>
              <TextItem active={current === 3}>
                {t`Bán Nguyên Căn`}
              </TextItem>
            </Link>
            <Link href='#' onClick={onClickNext} style={{ transform: 'rotate(180deg)' }}>
              <Arrow active={current < 3} name='up' />
            </Link>
          </Col>
          <Col md={4}>
            <Box flex justify='center' align='center'>
              <Image className='intro-step1' src={steps[0].img} width={267} height={480} alt="intro-step1" />
              <Image style={{ opacity: 0 }} className='intro-step2' src={steps[1].img} width={267} height={480} alt="intro infina"/>
              <Image style={{ opacity: 0 }} className='intro-step3' src={steps[2].img} width={267} height={480} alt="intro infina"/>
            </Box>
          </Col>
          <Col md={6}>

            {/* ========== step 1 ============== */}
            <Box style={{ position: 'absolute' }} className='intro-step1' flex column justify='center'>
              <Title lineHeight='normal' bold color='white'>
                {steps[0].title}
              </Title>
              <Text style={{ marginTop: 16 }} color='white'>
                {steps[0].desc}
              </Text>
              <GroupButtons handleShow={handleShow} />
            </Box>

            {/* ========== step 2 ============== */}
            <Box style={{ position: 'absolute', opacity: 0, display: 'none' }} className='intro-step2' flex column justify='center'>
              <Title lineHeight='normal' bold size={48} color='white'>
                {steps[1].title}
              </Title>
              <Text style={{ marginTop: 16 }} color='white'>
                {steps[1].desc}
              </Text>
              <GroupButtons handleShow={handleShow} />
            </Box>

            {/* ========== step 3 ============== */}
            <Box style={{ position: 'absolute', opacity: 0, display: 'none' }} className='intro-step3' flex column justify='center'>
              <Title lineHeight='normal' bold size={48} color='white'>
                {steps[2].title}
              </Title>
              <Text style={{ marginTop: 16 }} color='white'>
                {steps[2].desc}
              </Text>
              <GroupButtons handleShow={handleShow} />
            </Box>
          </Col>
        </Row>
      </Container>
    </MyContainer>
  )
}

export default DesktopIntro